import {
  createContext,
  createRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode, MutableRefObject } from 'react';
import { AgGridReact } from 'ag-grid-react';

interface IContextShape {
  /** The gridRef for the context */
  gridRef: MutableRefObject<AgGridReact>;
  /** Should be used in onGridReady function to determine if ag-grid is loaded */
  setReady(): void;
  /** True when onGridReady has been fired */
  ready: boolean;
}

const ComposedGridContext = createContext<IContextShape>({
  gridRef: createRef() as MutableRefObject<AgGridReact>,
  setReady: /* istanbul ignore next */ () => null,
  ready: false,
});

const emptyObj = {};

export const ComposedGridProvider = ({ children }: { children: ReactNode }) => {
  const gridRef = useRef<AgGridReact>(emptyObj as AgGridReact);
  const [ready, setReady] = useState(false);
  const value = useMemo(
    () => ({
      ready,
      setReady: () => setReady(true),
      gridRef,
    }),
    [ready]
  );
  return <ComposedGridContext.Provider value={value}>{children}</ComposedGridContext.Provider>;
};

export const useComposedGridContext = () => useContext(ComposedGridContext);

/* Tested in cypress */
/* istanbul ignore next */
export const useComposedGridState = ({
  gridRef,
  state,
}: {
  gridRef: MutableRefObject<AgGridReact>;
  state: any;
}) => {
  const getGridState = useCallback(() => {
    const filters = gridRef.current?.api?.getFilterModel();
    const page = gridRef.current?.api?.paginationGetCurrentPage();
    const pageSize = gridRef.current?.api?.paginationGetPageSize();
    const totalItems = gridRef.current?.api?.paginationGetRowCount();
    const columns = gridRef.current?.columnApi?.getColumnState();
    const rows = gridRef.current?.api?.getRenderedNodes().map(({ data }) => data);

    return {
      columns,
      filters,
      pagination: {
        page,
        pageSize,
        totalItems,
      },
      rows,
    };
  }, [gridRef]);

  const setGridState = useCallback(() => {
    gridRef.current?.api?.setFilterModel(state?.filters);
    gridRef.current?.api?.paginationGoToPage(state?.pagination?.page);
    gridRef.current?.api?.paginationSetPageSize(state?.pagination?.pageSize);
    gridRef.current?.columnApi?.applyColumnState({
      state: state?.columns,
      applyOrder: true,
    });
  }, [
    gridRef,
    state?.columns,
    state?.filters,
    state?.pagination?.page,
    state?.pagination?.pageSize,
  ]);
  return { getGridState, setGridState };
};

export default ComposedGridContext;
